import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { USE_QUERY_OPTIONS } from "../constants";

const CHART_TYPES = {
  permitted: {
    endpoint: "/api/ui-dashboard-permits-by-use-type",
    queryKey: "permits-by-use-type",
    gallonsField: "total_permitted_gallons",
    title: "Permitted Gallons by Use Type",
  },
  produced: {
    endpoint: "/api/ui-dashboard-production-by-use-type",
    queryKey: "production-by-use-type",
    gallonsField: "total_metered_gallons",
    title: "Produced Gallons by Use Type",
  },
};

export const usePermitsByUseType = () => {
  const [chartType, setChartType] = useState("permitted");
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedAquifer, setSelectedAquifer] = useState("all");

  const config = CHART_TYPES[chartType];

  const {
    data: permittedData,
    isLoading: permittedLoading,
    error: permittedError,
  } = useQuery(
    [CHART_TYPES.permitted.queryKey],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}${CHART_TYPES.permitted.endpoint}`
      );
      return response.data;
    },
    USE_QUERY_OPTIONS
  );

  const {
    data: producedData,
    isLoading: producedLoading,
    error: producedError,
  } = useQuery(
    [CHART_TYPES.produced.queryKey],
    async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}${CHART_TYPES.produced.endpoint}`
      );
      return response.data;
    },
    USE_QUERY_OPTIONS
  );

  const data = chartType === "permitted" ? permittedData : producedData;
  const isLoading =
    chartType === "permitted" ? permittedLoading : producedLoading;
  const error = chartType === "permitted" ? permittedError : producedError;

  const fiscalYears = useMemo(
    () =>
      data
        ? Array.from(new Set(data.map((item) => item.fiscal_year))).sort(
            (a, b) => b - a
          )
        : [],
    [data]
  );

  const aquiferGroups = useMemo(
    () =>
      data
        ? Array.from(
            new Map(
              data.map((item) => [
                item.aquifer_group_ndx,
                item.aquifer_group_name,
              ])
            )
          ).sort((a, b) => a[0] - b[0])
        : [],
    [data]
  );

  useEffect(() => {
    if (
      fiscalYears.length &&
      (selectedYear === null || !fiscalYears.includes(selectedYear))
    ) {
      setSelectedYear(fiscalYears[0]);
    }
  }, [fiscalYears, selectedYear]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter(
      (item) =>
        item.fiscal_year === selectedYear &&
        (selectedAquifer === "all" ||
          item.aquifer_group_ndx === selectedAquifer)
    );
  }, [data, selectedYear, selectedAquifer]);

  const aggregatedData = useMemo(() => {
    const gallonsField = config.gallonsField;
    const grouped = {};
    filteredData.forEach((item) => {
      const key = item.well_use_ndx;
      if (!grouped[key]) {
        grouped[key] = {
          well_use_ndx: item.well_use_ndx,
          well_use_name: item.well_use_name,
          total_gallons: 0,
        };
      }
      grouped[key].total_gallons += Number(item[gallonsField]);
    });
    return Object.values(grouped).sort(
      (a, b) => b.total_gallons - a.total_gallons
    );
  }, [filteredData, config.gallonsField]);

  const labels = aggregatedData.map((item) => item.well_use_name);
  const chartData = aggregatedData.map((item) => item.total_gallons);

  return {
    data: aggregatedData,
    labels,
    chartData,
    isLoading,
    error,
    fiscalYears,
    selectedYear,
    setSelectedYear,
    aquiferGroups,
    selectedAquifer,
    setSelectedAquifer,
    chartType,
    setChartType,
    chartTitle: config.title,
  };
};
